import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { PillBannerButton, type PillBannerButtonProps } from "components/footer/PillBannerButton";
import type { PolymorphicRef } from "types";

const AUTO_FOLLOW_DURATION = 6000;

export type AutoFollowButtonProps<T extends React.ElementType = "button"> = Omit<
  PillBannerButtonProps<T>,
  "kind" | "style"
> & {
  /** Text to display in the button */
  children: React.ReactNode;
  /** Called when the auto-follow timer completes (before auto-click) */
  onAutoFollowComplete?: () => void;
};

/**
 * A PillBannerButton with a 6-second countdown animation that auto-clicks when complete.
 * Used for auto-following presentation or screenshare. Should only be used inside a PillBanner.
 *
 * The button has an animated background that fills from left to right over 6 seconds,
 * with text that changes color as the fill progresses.
 *
 * The animation only starts when the tab has focus. If the tab is in the background,
 * it waits until focus is gained before starting.
 */
export const AutoFollowButton = forwardRef<
  HTMLButtonElement,
  Omit<AutoFollowButtonProps<"button">, "ref">
>(function AutoFollowButton(
  { children, className, onAutoFollowComplete, onClick, ...rest }: AutoFollowButtonProps<"button">,
  ref,
) {
  const innerRef = useRef<HTMLButtonElement>(null);
  const [wasClicked, setWasClicked] = useState(false);
  const [isTabFocused, setIsTabFocused] = useState(() => document.hasFocus());
  const elapsedTimeRef = useRef(0);
  const lastFocusTimeRef = useRef<number | null>(null);

  // Forward ref to allow parent to access the button
  useImperativeHandle(ref, () => innerRef.current as HTMLButtonElement);

  // Track tab focus/blur and accumulate elapsed time
  useEffect(() => {
    const handleFocus = () => {
      lastFocusTimeRef.current = Date.now();
      setIsTabFocused(true);
    };
    const handleBlur = () => {
      // Accumulate time spent focused
      if (lastFocusTimeRef.current !== null) {
        elapsedTimeRef.current += Date.now() - lastFocusTimeRef.current;
        lastFocusTimeRef.current = null;
      }
      setIsTabFocused(false);
    };

    // Initialize lastFocusTime if tab is already focused
    if (document.hasFocus()) {
      lastFocusTimeRef.current = Date.now();
    }

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Auto-click after animation completes if not already clicked
  // Only run timer when tab is focused, using remaining time
  useEffect(() => {
    if (wasClicked || !isTabFocused) return;

    const remainingTime = Math.max(0, AUTO_FOLLOW_DURATION - elapsedTimeRef.current);

    const timeoutId = setTimeout(() => {
      onAutoFollowComplete?.();
      innerRef.current?.click();
    }, remainingTime);

    return () => clearTimeout(timeoutId);
  }, [wasClicked, isTabFocused, onAutoFollowComplete]);

  const handleClick = (e: React.MouseEvent) => {
    setWasClicked(true);
    onClick?.(e as React.MouseEvent<HTMLButtonElement>);
  };

  // Pause animation when tab is not focused
  const animationStyle = {
    animationPlayState: isTabFocused ? "running" : "paused",
  } as const;

  return (
    <PillBannerButton
      className={twMerge("animate-follow-button border border-green", className)}
      ref={innerRef}
      style={{
        background: "linear-gradient(to right, white 50%, var(--green) 50%)",
        backgroundSize: "200% 100%",
        ...animationStyle,
      }}
      {...rest}
      onClick={handleClick}
    >
      {/* Extend span to cover full button area so text gradient matches button gradient exactly */}
      <span
        className="-mx-3 animate-follow-button bg-clip-text px-3"
        style={{
          background: "linear-gradient(to right, var(--black-tint-20) 50%, white 50%)",
          backgroundSize: "200% 100%",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          ...animationStyle,
        }}
      >
        {children}
      </span>
    </PillBannerButton>
  );
}) as <T extends React.ElementType = "button">(
  props: AutoFollowButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
